import {useEffect, useState} from "react";
import {postUser} from "../../helper/http.ts";
import styles from "./ReportPage.module.css";
import SearchableDropdown from "../../components/SearchableDropdown/SearchableDropdown";
import HavingQueryBuilder from "../../components/HavingQueryBuilder/HavingQueryBuilder.tsx";
import FieldsArrayBuilder from "../../components/FieldsArrayBuilder/FieldsArrayBuilder.tsx";
import ActionsArrayBuilder from "../../components/ActionsArrayBuilder/ActionsArrayBuilder.tsx";
import { useLanguage } from "../../context/LanguageContext";

export default function ReportPage() {
    const { t } = useLanguage();

    const [reports, setReports] = useState<any>([]);
    const [modules, setModules] = useState<any>([]);
    const [moduleFields, setModuleFields] = useState<any>([]);
    const [report, setReport] = useState<string>("");
    const [moduleName, setModuleName] = useState<string>("");
    const [reportName, setReportName] = useState<string>("");
    const [groupFieldValue, setGroupFieldValue] = useState<string>("");
    const [groupHavingValue, setGroupHavingValue] = useState<string>("");
    const [tableFields, setTableFields] = useState<any[]>([]);
    const [actions, setActions] = useState<any[]>([]);

    const loadReports = async () => {
        const response = await postUser("crud/list", {
            module: "report",
            page: "1",
            per_page: "100",
            sort_direction: "+",
            sort_field: "id",
        });

        setReports(response.data);
    }

    const loadModules = async () => {
        const response = await postUser("crud/list", {
            module: "module",
            page: "1",
            per_page: "100",
            sort_direction: "+",
            sort_field: "id",
        });

        setModules(response.data);
    }

    const loadModuleFields = async (moduleId: string) => {
        if (!moduleId) {
            setModuleFields([]);
            return;
        }

        const response = await postUser("crud/list", {
            module: "module_field",
            page: "1",
            per_page: "100",
            sort_direction: "+",
            sort_field: "order",
            filters: { module_id: +moduleId },
        });

        setModuleFields(response.data);
    }

    useEffect(() => {
        loadReports().catch(console.error);
        loadModules().catch(console.error);
    }, []);

    useEffect(() => {
        if (moduleName) {
            loadModuleFields(moduleName).catch(console.error);
        } else {
            setModuleFields([]);
        }
    }, [moduleName]);

    useEffect(() => {
        console.log(report);
        if (!report) {
            setReportName("");
            setModuleName("");
            setGroupHavingValue("");
            setGroupFieldValue("");
            setTableFields([]);
            setActions([]);
        } else {
            const reportData = reports.find((item: any) => item.id === +report);

            if (reportData) {
                if (typeof reportData.group_fields === 'string') reportData.group_fields = JSON.parse(reportData.group_fields);
                if (typeof reportData.table_fields === 'string') reportData.table_fields = JSON.parse(reportData.table_fields);
                if (typeof reportData.right_plugin === 'string') reportData.right_plugin = JSON.parse(reportData.right_plugin);
                
                setReportName(reportData.name);
                setModuleName(reportData.moduleId?.toString() || "");
                setGroupHavingValue(reportData.group_fields[0]?.having || "");
                setGroupFieldValue(reportData.group_fields[0]?.field || "");
                setTableFields(Array.isArray(reportData.table_fields) ? reportData.table_fields : []);
                setActions(Array.isArray(reportData.right_plugin) ? reportData.right_plugin : []);
            }
        }
    }, [report, reports]);

    const save = async () => {
        await postUser("crud/upsert", {
            module: "report",
            id: report ? +report : undefined,
            body: {
                "id": report ? +report : undefined,
                "group_fields": JSON.stringify([{
                    "field": groupFieldValue,
                    "having": groupHavingValue,
                }]),
                "table_fields": JSON.stringify(tableFields),
                "report_fields": "[]",
                "right_plugin": JSON.stringify(actions),
                "name": reportName,
                "moduleId": +moduleName,
            },
        });

        document.location.reload();
    }
    
    const reportOptions = [
        { label: t('newReport'), value: "" },
        ...reports.map((item: any) => ({
            label: item.name,
            value: item.id.toString()
        }))
    ];

    const moduleOptions = modules.map((item: any) => ({
        label: item.name,
        value: item.id.toString()
    }));

    const fieldOptions = moduleFields.map((item: any) => ({
        label: item.name,
        value: item.name
    }));

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>{t('reports')}</h1>
            </div>

            <div className={styles.selectGroup}>
                <SearchableDropdown
                    label={t('selectReport')}
                    options={reportOptions}
                    value={report}
                    onChange={(value) => setReport(value)}
                />
            </div>

            <div className={styles.formCard}>
                <h2 className={styles.sectionTitle}>{t('basicInformation')}</h2>
                
                <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>{t('name')}</label>
                    <input 
                        type="text" 
                        className={styles.input}
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <SearchableDropdown
                        label={t('module')}
                        options={moduleOptions}
                        value={moduleName}
                        onChange={(value) => {
                            setModuleName(value);
                            setGroupFieldValue(""); 
                        }}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <SearchableDropdown
                        label={t('groupField')}
                        options={fieldOptions}
                        value={groupFieldValue}
                        onChange={(value) => setGroupFieldValue(value)}
                        disabled={!moduleName}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <HavingQueryBuilder
                        value={groupHavingValue}
                        onChange={setGroupHavingValue}
                        fields={fieldOptions}
                    />
                </div>
            </div>

            <div className={styles.formCard}>
                <FieldsArrayBuilder
                    value={tableFields}
                    onChange={setTableFields}
                    availableFields={fieldOptions}
                    availableModules={moduleOptions}
                    title={t('tableFields')}
                />
            </div>

            <div className={styles.formCard}>
                <ActionsArrayBuilder
                    value={actions}
                    onChange={setActions}
                    title={t('actions')}
                />
            </div>

            <div className={styles.formCard}>
                <button className={styles.saveButton} onClick={() => save()}>
                    {t('saveReport')}
                </button>
            </div>
        </div>
    );
}